import React, { type ForwardedRef } from 'react';

import LoadingIndicator from '@/components/loading-indicator';
import { Link } from '@/components/link';

import { StyledCompareButton, StyledPrimaryButton, StyledSecondaryButton, StyledTertiaryButton } from './styles';
import { handleClick } from './utils/handle-click';
import { type UrlObject } from 'url';
import { type TrackingEventObject } from '@/types/tracking';

type BaseProps = {
  asPath?: UrlObject | string | null;
  href?: string;
  query?: UrlObject['query'];
  target?: React.HTMLAttributeAnchorTarget;
  children?: React.JSX.Element | React.ReactNode;
  disabled?: boolean;
  isLoading?: boolean;
  buttonAsDiv?: boolean;
  className?: string;
  startIcon?: React.JSX.Element | React.ReactNode;
  endIcon?: React.JSX.Element | React.ReactNode;
  onClick?: React.MouseEventHandler;
  fullWidth?: boolean;
  type?: React.ButtonHTMLAttributes<HTMLButtonElement>['type'];
  disableRipple?: boolean;
  count?: number;
};

type WithEventText = BaseProps & {
  eventText: string;
  tracking?: undefined;
};

type WithTracking = BaseProps & {
  tracking: TrackingEventObject;
  eventText?: string;
};

export type LinkWrappingProps = WithEventText | WithTracking;

type ForwardedButtonProps = {
  href?: string;
};

type WrappableButton =
  | typeof StyledPrimaryButton
  | typeof StyledSecondaryButton
  | typeof StyledTertiaryButton
  | typeof StyledCompareButton;

const handleLinkWrapping = (WrappableButtonComponent: WrappableButton, props: LinkWrappingProps) => {
  /*
    Using asPath instead of as prop here to avoid clashing with styled-components's
    reserved "as" prop: https://styled-components.com/docs/api#as-polymorphic-prop
  */
  const { asPath, href, query, target, children, disabled, isLoading, buttonAsDiv, onClick, ...rest } = props;

  const isInternalLink = href && href.startsWith('/');

  const eventText = 'eventText' in props ? props.eventText : undefined;
  const tracking = 'tracking' in props ? props.tracking : undefined;

  const LinkButton = React.forwardRef(({ href }: ForwardedButtonProps, ref: ForwardedRef<HTMLButtonElement>) => {
    return (
      <WrappableButtonComponent
        href={href}
        ref={ref}
        disabled={disabled}
        onClick={(e) => handleClick({ event: e, eventText, tracking, eventUrl: href, onClick })}
        {...rest}
      >
        {isLoading ? <LoadingIndicator /> : children}
      </WrappableButtonComponent>
    );
  });

  LinkButton.displayName = 'LinkButton';

  if (href && !disabled) {
    if (isInternalLink) {
      return (
        <Link
          legacyBehavior
          passHref
          href={query ? { pathname: href, query } : href}
          as={asPath ?? undefined}
          {...rest}
        >
          <LinkButton />
        </Link>
      );
    }

    return (
      <WrappableButtonComponent
        component="a"
        href={href}
        target={target || '_blank'}
        rel={!target ? 'noopener noreferrer' : undefined}
        onClick={(e) => handleClick({ event: e, eventText, eventUrl: href, tracking, onClick })}
        {...rest}
      >
        {isLoading ? <LoadingIndicator /> : children}
      </WrappableButtonComponent>
    );
  }

  const component = buttonAsDiv ? 'div' : undefined;

  return (
    <WrappableButtonComponent
      disabled={disabled}
      component={component}
      {...rest}
      onClick={(e) => handleClick({ event: e, eventText, tracking, onClick })}
    >
      {isLoading ? <LoadingIndicator /> : children}
    </WrappableButtonComponent>
  );
};

const PrimaryButton = (props: LinkWrappingProps) => handleLinkWrapping(StyledPrimaryButton, props);
const SecondaryButton = (props: LinkWrappingProps) => handleLinkWrapping(StyledSecondaryButton, props);
const TertiaryButton = (props: LinkWrappingProps) => handleLinkWrapping(StyledTertiaryButton, props);
const CompareButton = (props: LinkWrappingProps) => handleLinkWrapping(StyledCompareButton, props);

export { PrimaryButton, SecondaryButton, TertiaryButton, CompareButton };
